import { useEffect } from 'react'

/**
 * Sets the page `<title>` and `og:title`/`og:description` while a component is mounted,
 * restoring the app's default title on unmount.
 *
 * No react-helmet in this app -- this is the one screen (the public job posting page,
 * shared to Facebook/LinkedIn) that needs its preview to say something other than
 * "FMCG ERP Suite", since those platforms scrape these tags rather than take any text
 * we pass in the share link itself. See `features/recruitment/share.ts`.
 */
export function useDocumentMeta(title: string | undefined, description: string | undefined) {
  useEffect(() => {
    if (!title) return

    const previousTitle = document.title
    document.title = title

    const ogTitle = ensureMetaTag('og:title')
    const previousOgTitle = ogTitle.getAttribute('content')
    ogTitle.setAttribute('content', title)

    let ogDescription: HTMLMetaElement | null = null
    let previousOgDescription: string | null = null
    if (description) {
      ogDescription = ensureMetaTag('og:description')
      previousOgDescription = ogDescription.getAttribute('content')
      ogDescription.setAttribute('content', description)
    }

    return () => {
      document.title = previousTitle
      setOrRemoveMetaTag(ogTitle, previousOgTitle)
      if (ogDescription) setOrRemoveMetaTag(ogDescription, previousOgDescription)
    }
  }, [title, description])
}

function ensureMetaTag(property: string): HTMLMetaElement {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    document.head.appendChild(tag)
  }
  return tag
}

function setOrRemoveMetaTag(tag: HTMLMetaElement, previousContent: string | null) {
  if (previousContent === null) {
    tag.remove()
  } else {
    tag.setAttribute('content', previousContent)
  }
}
